// src/pages/tasks/task-notification-page/TaskNotificationPage.tsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Col,
  Divider,
  Form,
  Input,
  Row,
  Select,
  Space,
  Typography,
  message,
} from "antd";
import {
  PlusOutlined,
  MinusCircleOutlined,
  SendOutlined,
} from "@ant-design/icons";
import { useHistory, useParams } from "react-router-dom";
import { getUserId, getUserName } from "../../../services/lib/utils/auth.utils";
import { ApiRequest } from "../../../services/api/api";

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const TaskNotificationPage: React.FC = () => {
  const history = useHistory();
  const params = useParams<{ taskId?: string }>();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [allUsers, setAllUsers] = useState<{ value: string; label: string }[]>([]);

  // 从URL参数或props获取taskId
  const taskId = params.taskId;
  const [taskInfo, setTaskInfo] = useState<any>(null);

  useEffect(() => {
    document.title = "Create Notification";
    
    // 初始化创建者
    const currentUser = getUserName() || "执行操作的账号 (不可更改)";
    form.setFieldsValue({
      creator: currentUser,
      visibility: "To Selected people",
      people: [{ userId: undefined }],
    });

    loadUsers();

    if (taskId) {
      loadTaskInfo(taskId);
    }
  }, [taskId]);

  const loadTaskInfo = async (id: string) => {
    try {
      const response = await ApiRequest({
        otherUrl: "TaskManagement/GetTaskById",
        type: "get",
        params: { taskId: parseInt(id, 10) },
      });
      const data = response?.data?.data ?? response?.data;
      if (!data) throw new Error("Task not found");
      setTaskInfo(data);
      const currentUser = getUserName() || "当前用户";
      form.setFieldsValue({
        creator: currentUser,
        visibility: "To Selected people",
        people: [{ userId: undefined }],
        content: `${data.title ?? data.Title ?? ""}\nNotification content...`,
      });
    } catch (error: any) {
      message.error(error?.message || "Failed to load task info");
    }
  };

  const loadUsers = async () => {
    if (taskId) {
      // ensure task info loaded
    }
    try {
      const result = await ApiRequest({
        otherUrl: "Employee/GetAllEmployee",
        type: "get",
      });
      const employees = result?.data?.data ?? result?.data ?? [];
      const options = (employees || []).map((emp: any) => {
        const name = `${emp.firstName ?? emp.FirstName ?? ""} ${emp.lastName ?? emp.LastName ?? ""}`.trim();
        return {
          value: String(emp.employeeId ?? emp.EmployeeId),
          label: name || emp.userName || emp.UserName || `Employee ${emp.employeeId ?? emp.EmployeeId}`,
        };
      });
      setAllUsers(options);
    } catch (error: any) {
      message.error(error?.message || "Failed to load employees");
    }
  };

  type Participant = { userId: number; name: string; role: string };

  const participants = useMemo<Participant[]>(() => {
    const data = taskInfo || {};
    const list = data.participants ?? data.Participants ?? [];
    return (list || []).map((p: any) => {
      const idValue = parseInt(String(p.userId ?? p.UserId ?? 0), 10);
      return {
        userId: Number.isFinite(idValue) ? idValue : 0,
        name: p.name ?? p.Name ?? "",
        role: (p.role ?? p.Role ?? "").toLowerCase(),
      };
    });
  }, [taskInfo]);

  const resolveRecipients = (values: any) => {
    const visibility = values.visibility;
    if (!taskInfo) return [] as number[];
    if (visibility === "All") {
      return participants.map((p) => p.userId).filter((id) => Number.isFinite(id) && id > 0);
    }
    if (visibility === "Manager Only") {
      return participants
        .filter((p) => p.role === "manager")
        .map((p) => p.userId)
        .filter((id) => Number.isFinite(id) && id > 0);
    }
    if (visibility === "Assignees Only") {
      return participants
        .filter((p) => p.role === "assignee")
        .map((p) => p.userId)
        .filter((id) => Number.isFinite(id) && id > 0);
    }
    const selected = values.people || [];
    return selected
      .map((p: any) => (p?.userId ? parseInt(String(p.userId), 10) : undefined))
      .filter((id: number | undefined): id is number => Number.isFinite(id) && (id ?? 0) > 0);
  };

  const handleSubmit = async (values: any) => {
    try {
      setLoading(true);
      
      const creatorIdRaw = getUserId();
      const createdBy = creatorIdRaw ? parseInt(String(creatorIdRaw), 10) : 0;
      if (!createdBy) {
        throw new Error("Current user is not identified.");
      }
      if (!taskId) {
        throw new Error("Task id is missing.");
      }

      const notifyUserIds = resolveRecipients(values);
      if ((!notifyUserIds || notifyUserIds.length === 0) && values.visibility === "To Selected people") {
        throw new Error("Please select at least one recipient.");
      }

      await ApiRequest({
        otherUrl: "TaskComment/AddComment",
        method: "post",
        data: {
          taskId: parseInt(taskId, 10),
          createdBy,
          commentType: "Notice",
          content: values.content,
          notifyUserIds,
        },
        isShowSpinner: true,
      });

      message.success("Notification created successfully!");
      history.goBack();
    } catch (error: any) {
      console.error("Failed to create notification", error);
      const errMsg = error?.message;
      message.error(errMsg && errMsg !== "[object Object]" ? errMsg : "Failed to create notification");
    } finally {
      setLoading(false);
    }
  };

  const filteredUserOptions = useMemo(() => allUsers, [allUsers]);

  return (
    <div style={{ padding: "1rem", maxWidth: "1400px", margin: "0 auto" }}>
      <Row gutter={24}>
        {/* 左侧：表单区域 */}
        <Col xs={24} lg={16}>
          <Card>
            <Title level={3}>Notification</Title>
            <Divider />

            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              autoComplete="off"
            >
              {/* Creator - 不可编辑 */}
              <Form.Item
                label="Creator"
                name="creator"
                rules={[{ required: true }]}
              >
                <Input disabled style={{ color: "#666" }} />
              </Form.Item>

              {/* Visibility - 可见性 */}
              <Form.Item
                label="Visibility"
                name="visibility"
                rules={[{ required: true, message: "Please select visibility" }]}
              >
                <Select style={{ width: "100%" }}>
                  <Option value="All">All Task Members</Option>
                  <Option value="To Selected people">To Selected people</Option>
                  <Option value="Manager Only">Manager Only</Option>
                  <Option value="Assignees Only">Assignees Only</Option>
                </Select>
              </Form.Item>

              {/* 人员选择器 - 动态列表 */}
              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) =>
                  prevValues.visibility !== currentValues.visibility
                }
              >
                {({ getFieldValue }) =>
                  getFieldValue("visibility") === "To Selected people" ? (
                    <Form.List name="people">
                      {(fields, { add, remove }) => (
                        <>
                          {fields.map(({ key, name, ...restField }, index) => (
                            <Row
                              key={key}
                              gutter={8}
                              style={{ marginBottom: 8 }}
                              align="middle"
                            >
                              <Col span={12}>
                                <Form.Item
                                  {...restField}
                                  name={[name, "userId"]}
                                  style={{ marginBottom: 0 }}
                                  rules={[{ required: true, message: "Please select a user" }]}
                                >
                                  <Select
                                    placeholder="Select user"
                                    showSearch
                                    allowClear
                                    filterOption={(input, option) =>
                                      String(option?.label || '')
                                        .toLowerCase()
                                        .includes(input.toLowerCase())
                                    }
                                    options={filteredUserOptions}
                                  />
                                </Form.Item>
                              </Col>

                              <Col span={4}>
                                <Button
                                  type="dashed"
                                  onClick={() => add()}
                                  icon={<PlusOutlined />}
                                  size="small"
                                  style={{ width: "100%" }}
                                />
                              </Col>

                              <Col span={4}>
                                {fields.length > 1 && (
                                  <MinusCircleOutlined
                                    style={{ color: "#ff4d4f", fontSize: "18px" }}
                                    onClick={() => remove(name)}
                                  />
                                )}
                              </Col>
                            </Row>
                          ))}
                          {fields.length === 0 && (
                            <Button
                              type="dashed"
                              onClick={() => add()}
                              block
                              icon={<PlusOutlined />}
                            >
                              Add People
                            </Button>
                          )}
                        </>
                      )}
                    </Form.List>
                  ) : null
                }
              </Form.Item>

              {/* Content - 通知内容 */}
              <Form.Item
                label="Content"
                name="content"
                rules={[
                  { required: true, message: "Please enter notification content" },
                ]}
              >
                <TextArea
                  rows={6}
                  placeholder="Enter notification content..."
                  style={{ fontSize: "14px" }}
                />
              </Form.Item>

              <Divider />

              {/* 操作按钮 */}
              <Form.Item>
                <Space>
                  <Button
                    type="primary"
                    htmlType="submit"
                    icon={<SendOutlined />}
                    loading={loading}
                    size="large"
                  >
                    Confirm
                  </Button>
                  <Button size="large" onClick={() => history.goBack()}>
                    Cancel
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </Card>
        </Col>

        {/* 右侧提示框 */}
        <Col xs={24} lg={8}>
          <Card
            style={{
              position: "sticky",
              top: "2rem",
              backgroundColor: "#e6f7ff",
              borderColor: "#91d5ff",
            }}
          >
            <Title level={5}>💡 使用提示</Title>
            <ul style={{ paddingLeft: "1.5rem", margin: 0 }}>
              <li>创建者为当前登录用户，不可修改</li>
              <li>可见性选择"To Selected people"时可自定义接收人员</li>
              <li>内容支持多行文本，可包含任务标题和更新信息</li>
              <li>通知将发送给选定的人员</li>
            </ul>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default TaskNotificationPage;

